import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import { assistentApi, type AssistentEinstellungen, type AssistentEinstellungenAenderung } from '@/lib/assistent/assistent.api'
import { useAssistent } from '@/lib/assistent/assistent.store'
import { cardTitle, formCard } from '@/lib/form-styles'
import { FeldZeile, SchalterZeile, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'

/** Der Menüpunkt je Bereich — die Maske benutzt dessen Beschriftung, keine eigene. */
const MENUE: Record<string, string> = {
  time: 'nav.timeTracking',
  crm: 'nav.clients',
  articles: 'nav.articles',
  calculation: 'nav.calculation',
  correspondence: 'nav.correspondence',
  incoming_invoices: 'nav.incomingInvoices',
  inbox: 'nav.inbox',
  payroll: 'nav.payroll',
  settings: 'nav.settings',
}

/** In der Maske heißt ein Bereich, wie das Menü ihn nennt; der Projektbereich so, wie der Betrieb ihn nennt (AP-16). */
function useBereichName() {
  const { t } = useTranslation()
  const terms = useTerminology()
  return (key: string) => (key === 'projects' ? terms.management : MENUE[key] ? t(MENUE[key]) : key)
}

/**
 * AP-18 12.2 (Phase G) — Karte **„Grenzen und Datenschutz"** des Assistenten: die Tagesgrenze je
 * Benutzer, wie lange Gespräche aufbewahrt werden, und welche Bereiche der Assistent nicht anfasst.
 *
 * Gespeichert wird feldweise ohne Knopf (`.claude/rules/speichern.md`, Fall 1): Zahlen beim Verlassen
 * des Felds, Häkchen sofort — über die Speicherstelle, mit Stand gegen Überschreiben.
 *
 * Die Bereiche kommen vom Server und sind nur die, die der Betrieb sperren **kann** (zu gebuchten
 * Bausteinen). Die Maske schickt deren ganze Liste; was sie nicht zeigt, lässt der Server stehen.
 */
export function AssistentGrenzen({ e, darfAendern, onGeaendert }: { e: AssistentEinstellungen; darfAendern: boolean; onGeaendert: (e: AssistentEinstellungen) => void }) {
  const { t } = useTranslation()
  const ladeStand = useAssistent((s) => s.ladeStand)
  const bereichName = useBereichName()

  // Die Bereiche als Häkchen je Schlüssel — die Speicherstelle kennt Felder, keine Listen.
  const daten = useMemo(
    () => ({ dailyTokenLimitPerUser: e.dailyTokenLimitPerUser, retentionDays: e.retentionDays, gesperrt: Object.fromEntries(e.areas.map((a) => [a.key, a.excluded])), updatedAt: e.updatedAt }) as unknown as Datensatz,
    [e],
  )
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'aiAssistantSettings',
    bezeichnung: t('assistant.limits.title'),
    daten,
    schreibe: async (patch, stand) => {
      const { gesperrt, ...rest } = patch as { gesperrt?: Record<string, boolean> } & AssistentEinstellungenAenderung
      const dto: AssistentEinstellungenAenderung = { ...rest }
      // Ein Häkchen ändert die Liste — gesendet wird sie ganz, aus dem Stand der Maske und der Änderung.
      if (gesperrt) dto.excludedAreas = e.areas.filter((a) => gesperrt[a.key] ?? a.excluded).map((a) => a.key)
      const neu = await assistentApi.einstellungenAendern(dto, stand)
      onGeaendert(neu)
      // Die Seitenleiste soll sofort wissen, ob die Tagesgrenze den Benutzer jetzt sperrt.
      void ladeStand()
      return { data: { dailyTokenLimitPerUser: neu.dailyTokenLimitPerUser, retentionDays: neu.retentionDays, gesperrt: Object.fromEntries(neu.areas.map((a) => [a.key, a.excluded])), updatedAt: neu.updatedAt } } as unknown as Datensatz
    },
  }) as unknown as Speicherstelle<unknown>

  const zahl = (n: number) => n.toLocaleString('de-DE')
  const grenze = useFeld<number | null>(stelle, 'dailyTokenLimitPerUser', {
    art: 'zahl',
    stellen: 0,
    label: t('assistant.limits.daily'),
    gesperrt: !darfAendern,
    pruefe: (w) => (w === null || (Number.isInteger(w) && w >= e.limits.dailyMin && w <= e.limits.dailyMax) ? null : t('assistant.limits.dailyRange', { min: zahl(e.limits.dailyMin), max: zahl(e.limits.dailyMax) })),
  })
  const frist = useFeld<number | null>(stelle, 'retentionDays', {
    art: 'zahl',
    stellen: 0,
    label: t('assistant.limits.retention'),
    pflicht: true,
    gesperrt: !darfAendern,
    pruefe: (w) => (w !== null && Number.isInteger(w) && w >= e.limits.retentionMin && w <= e.limits.retentionMax ? null : t('assistant.limits.retentionRange', { min: e.limits.retentionMin, max: e.limits.retentionMax })),
  })

  return (
    <div className={formCard}>
      <div className="mb-1 flex items-center gap-2">
        <ShieldCheck size={18} className="text-primary" />
        <h2 className={cardTitle}>{t('assistant.limits.title')}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{t('assistant.limits.description')}</p>

      <div className="mt-4 space-y-3">
        <FeldZeile label={t('assistant.limits.daily')} status={grenze} hilfe={t('assistant.limits.dailyHelp')}>
          <input className={`${feldKlasse} sm:!w-40`} inputMode="numeric" placeholder={t('assistant.limits.dailyNone')} {...grenze.props} />
        </FeldZeile>
        <FeldZeile label={t('assistant.limits.retention')} status={frist} hilfe={t('assistant.limits.retentionHelp')}>
          <input className={`${feldKlasse} sm:!w-40`} inputMode="numeric" {...frist.props} />
        </FeldZeile>
      </div>

      <div className="mt-5">
        <h3 className="text-sm font-medium text-foreground">{t('assistant.limits.areas')}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('assistant.limits.areasHelp')}</p>
        <div className="mt-3 space-y-2">
          {e.areas.length === 0 && <p className="text-sm text-muted-foreground">{t('assistant.limits.areasEmpty')}</p>}
          {e.areas.map((a) => (
            <BereichZeile key={a.key} stelle={stelle} bereich={a.key} name={bereichName(a.key)} darfAendern={darfAendern} />
          ))}
        </div>
      </div>
    </div>
  )
}

function BereichZeile({ stelle, bereich, name, darfAendern }: { stelle: Speicherstelle<unknown>; bereich: string; name: string; darfAendern: boolean }) {
  const { t } = useTranslation()
  const feld = useFeld<boolean>(stelle, `gesperrt.${bereich}`, { art: 'schalter', label: name, gesperrt: !darfAendern })
  return <SchalterZeile feld={feld} label={t('assistant.limits.areaExcluded', { area: name })} />
}
